import React from "react";
import { useRouteError } from "react-router-dom";
import { AlertTriangle, Copy, Check, MessageCircle } from "lucide-react";
import { captureException } from "../../../lib/error-tracking";

declare global {
	interface Window {
		Intercom: (command: string, message?: string) => void;
	}
}

interface ErrorFallbackProps {
	error?: Error;
	componentStack?: string;
	resetError?: () => void;
	variant?: "full" | "inline";
}

const buildSupportMessage = (
	error: Error,
	componentStack?: string
): string => {
	const stack = error.stack ?? "(kein Stack Trace verfügbar)";
	const compStack = componentStack?.trim()
		? componentStack
		: "(kein Component Stack verfügbar)";

	return `Hallo Support-Team,

in der Anwendung ist ein unerwarteter Fehler aufgetreten.

Seite: ${window.location.href}
Zeitpunkt: ${new Date().toLocaleString("de-DE")}
User Agent: ${navigator.userAgent}

Fehler: ${error.message}

Stack Trace:
${stack}

Component Stack:
${compStack}

Vielen Dank für die Hilfe!`;
};

export const ErrorFallback: React.FC<ErrorFallbackProps> = ({
	error: propError,
	componentStack,
	resetError,
	variant = "inline",
}) => {
	const [copied, setCopied] = React.useState(false);
	const [supportSent, setSupportSent] = React.useState(false);

	let routeError: Error | null = null;
	try {
		const caught = useRouteError();
		if (caught) {
			routeError =
				caught instanceof Error ? caught : new Error(String(caught));
		}
	} catch {
		// Not inside a route error context
	}

	const error = propError ?? routeError;
	const isErrorElement = !propError && !!routeError;

	const capturedRef = React.useRef(false);
	if (isErrorElement && error && !capturedRef.current) {
		capturedRef.current = true;
		captureException(error);
	}

	const handleGoHome = () => {
		window.location.href = "/";
	};

	const handleRetry = () => {
		if (resetError) {
			resetError();
		} else {
			window.location.reload();
		}
	};

	const handleCopy = () => {
		if (!error) return;
		const text = `${error.message}\n\n${componentStack ?? error.stack}`;
		navigator.clipboard.writeText(text).then(() => {
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		});
	};

	const handleContactSupport = () => {
		if (!error) return;
		const message = buildSupportMessage(error, componentStack);
		if (window.Intercom) {
			window.Intercom("showNewMessage", message);
		} else {
			navigator.clipboard.writeText(message);
			setSupportSent(true);
			setTimeout(() => setSupportSent(false), 2500);
		}
	};

	return (
		<div
			className={
				variant === "full"
					? "flex flex-col items-center justify-center h-screen bg-stone-100"
					: "flex flex-col items-center justify-center h-full min-h-[400px]"
			}
			data-testid="error-fallback"
		>
			<div className="flex flex-col items-center gap-4 max-w-xl text-center px-4">
				<AlertTriangle className="h-12 w-12 text-amber-500" />
				<h2 className="text-xl font-semibold text-gray-900">
					Etwas ist schiefgelaufen
				</h2>
				<p className="text-sm text-gray-500">
					Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie
					es erneut.
				</p>
				<div className="flex flex-wrap justify-center gap-3 mt-2">
					<button
						onClick={handleGoHome}
						className="inline-flex items-center justify-center rounded-md text-sm font-medium h-10 px-4 py-2 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 transition-colors"
						data-testid="error-fallback-home"
						aria-label="Zurück zur Startseite"
					>
						Zurück zur Startseite
					</button>
					<button
						onClick={handleRetry}
						className="inline-flex items-center justify-center rounded-md text-sm font-medium h-10 px-4 py-2 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 transition-colors"
						data-testid="error-fallback-retry"
						aria-label="Nochmal versuchen"
					>
						Nochmal versuchen
					</button>
					{error && (
						<button
							onClick={handleContactSupport}
							className="inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium h-10 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white transition-colors"
							data-testid="error-fallback-support"
							aria-label="Support-Chat mit Fehlerbericht öffnen"
						>
							<MessageCircle className="h-4 w-4" />
							{supportSent
								? "In Zwischenablage kopiert"
								: "Support-Chat öffnen"}
						</button>
					)}
				</div>
				{error && (
					<p className="text-xs text-gray-500 px-1 max-w-md">
						In den Chat wird automatisch der Fehlerbericht mit
						Technischen Details und Seiteninformationen eingefügt.
					</p>
				)}

				{error && (
					<details
						className="mt-4 w-full text-left"
						open={import.meta.env.DEV}
					>
						<summary className="cursor-pointer text-xs text-gray-500 hover:text-gray-700">
							Technische Details (Stack Trace)
						</summary>
						<div className="relative mt-2">
							<button
								onClick={handleCopy}
								className="absolute top-2 right-2 p-1 rounded hover:bg-red-100 text-red-400 hover:text-red-600 transition-colors"
								data-testid="error-fallback-copy"
								aria-label="Stack Trace kopieren"
							>
								{copied ? (
									<Check className="h-3.5 w-3.5" />
								) : (
									<Copy className="h-3.5 w-3.5" />
								)}
							</button>
							<pre className="text-xs text-red-600 bg-red-50 p-3 pr-8 rounded overflow-auto max-h-64 whitespace-pre-wrap">
								{error.message}
								{"\n\n"}
								{error.stack ?? "(kein Stack Trace verfügbar)"}
								{componentStack && (
									<>
										{"\n\nComponent Stack:"}
										{componentStack}
									</>
								)}
							</pre>
						</div>
					</details>
				)}
			</div>
		</div>
	);
};
